import React, { useState, useEffect } from 'react';
import { userService } from '../services/firebaseService';

interface DeletedUser {
  id: string;
  originalUserId: string;
  name: string;
  email: string;
  deletedAt: any;
  deletedBy: string;
  deletionReason: string;
  originalCreatedAt: any;
  totalSubscribers: number;
  userData: any;
}

const DeletedUsersPage: React.FC = () => {
  const [deletedUsers, setDeletedUsers] = useState<DeletedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadDeletedUsers();
  }, []);

  const loadDeletedUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getDeletedUsers(100); // 100 silinen kullanıcı getir
      setDeletedUsers(response.deletedUsers);
    } catch (error) {
      console.error('Silinen kullanıcıları yükleme hatası:', error);
      alert('Silinen kullanıcıları yüklerken hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreUser = async (deletedUser: DeletedUser) => {
    if (!window.confirm(`"${deletedUser.name}" adlı kullanıcıyı geri yüklemek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      setRestoring(deletedUser.id);
      
      await userService.restoreUser(deletedUser.id, deletedUser.originalUserId);
      
      alert(`"${deletedUser.name}" adlı kullanıcı başarıyla geri yüklendi!`);
      
      // Listeyi yenile
      await loadDeletedUsers();
    } catch (error) {
      console.error('Kullanıcı geri yükleme hatası:', error);
      alert('Kullanıcı geri yüklenirken hata oluştu!');
    } finally {
      setRestoring(null);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Bilinmiyor';
    
    try {
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        date = new Date(timestamp);
      }
      
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Tarih formatı hatası:', error);
      return 'Geçersiz tarih';
    }
  };

  const filteredUsers = deletedUsers.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.deletionReason.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        fontSize: '1.2rem'
      }}>
        <div style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          animation: 'spin 1s linear infinite',
          marginRight: '1rem'
        }}></div>
        Silinen kullanıcılar yükleniyor...
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div>
          <h1 style={{ 
            margin: 0, 
            color: '#2c3e50',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            🗑️ Silinen Kullanıcılar
          </h1>
          <p style={{ 
            margin: '0.5rem 0 0 0', 
            color: '#666',
            fontSize: '1rem'
          }}>
            Admin tarafından silinen kullanıcıların listesi
          </p>
        </div>
        
        <div style={{
          backgroundColor: '#dc3545',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
            {deletedUsers.length}
          </div>
          <div style={{ fontSize: '0.9rem' }}>
            Silinen Kullanıcı
          </div>
        </div>
      </div>

      {/* Arama */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <input
          type="text"
          placeholder="Kullanıcı adı, e-posta veya silme sebebi ile ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '1rem',
            border: '2px solid #e0e6ed',
            borderRadius: '8px',
            fontSize: '1rem',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Kullanıcı Listesi */}
      {filteredUsers.length === 0 ? (
        <div style={{
          backgroundColor: 'white',
          padding: '3rem',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
          <h3 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>
            {searchTerm ? 'Arama sonucu bulunamadı' : 'Silinen kullanıcı yok'}
          </h3>
          <p style={{ color: '#666', margin: 0 }}>
            {searchTerm 
              ? 'Farklı arama terimleri deneyebilirsiniz.'
              : 'Şu an için silinmiş kullanıcı bulunmuyor.'
            }
          </p>
        </div>
      ) : (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          {/* Tablo Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 0.8fr 1fr 120px',
            gap: '1rem',
            padding: '1.5rem',
            backgroundColor: '#f8f9fa',
            borderBottom: '2px solid #e9ecef',
            fontWeight: '600',
            color: '#2c3e50'
          }}>
            <div>👤 Kullanıcı</div>
            <div>📧 E-posta</div>
            <div>📅 Silinme Tarihi</div>
            <div>👥 Abone Sayısı</div>
            <div>📝 Silme Sebebi</div>
            <div style={{ textAlign: 'center' }}>⚡ İşlemler</div>
          </div>

          {/* Kullanıcı Satırları */}
          {filteredUsers.map((deletedUser, index) => (
            <div
              key={deletedUser.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 0.8fr 1fr 120px',
                gap: '1rem',
                padding: '1.5rem',
                borderBottom: index < filteredUsers.length - 1 ? '1px solid #f0f0f0' : 'none',
                backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
              }}
            >
              <div>
                <div style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '0.25rem' }}>
                  {deletedUser.name}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                  ID: {deletedUser.originalUserId.slice(0, 8)}...
                </div>
              </div>
              
              <div style={{ color: '#495057' }}>
                {deletedUser.email}
              </div>
              
              <div>
                <div style={{ fontWeight: '500', color: '#dc3545' }}>
                  {formatDate(deletedUser.deletedAt)}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                  {deletedUser.deletedBy} tarafından
                </div>
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <span style={{
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  {deletedUser.totalSubscribers || 0}
                </span>
              </div>
              
              <div style={{ 
                fontSize: '0.9rem', 
                color: '#495057',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }} title={deletedUser.deletionReason}>
                {deletedUser.deletionReason}
              </div>
              
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => handleRestoreUser(deletedUser)}
                  disabled={restoring === deletedUser.id}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    cursor: restoring === deletedUser.id ? 'not-allowed' : 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: '500',
                    opacity: restoring === deletedUser.id ? 0.6 : 1
                  }}
                  title="Kullanıcıyı geri yükle"
                >
                  {restoring === deletedUser.id ? '⏳' : '♻️ Geri Yükle'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CSS Animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default DeletedUsersPage; 